import { Logger } from 'common/log'
import { Subclip } from 'elements-sdk/src'
import { BaseInterface, loadAssetContentsPages } from './base'

export class ResolvePanelInterface extends BaseInterface {
  logger = new Logger(__filename)
  systemPath = window['ResolveSystemPath']
  native = true

  private resolve: any
  private projectmanager: any
  private project: any
  private pool: any
  private media: any
  private wi: any

  constructor (injector) {
    super(injector)

    this.wi = window['WorkflowIntegration']
  }

  async init () {
    this.logger.info('Resolve init')
    this.active = this.wi.Initialize('com.syslink.elements')
    this.platform = this.getPlatform()
    this.resolve = this.wi.GetResolve()
    this.media = this.resolve.GetMediaStorage()
    this.projectmanager = this.resolve.GetProjectManager()
    this.project = this.getProject()
    this.pool = this.project.GetMediaPool()

    this.app = this.getApp()
    this.active = true
  }

  getFS () {
    return {
      ...window['nodeFS'],

      writeFile: async function (path, xml) {
        return new Promise((resolve, reject) => {
          window['nodeFS'].writeFile(path, xml, (err) => {
            if (err) {
              reject(err)
            } else {
              resolve(path)
            }
          })
        })
      },
    }
  }

  getApp () {
    return 'Resolve'
  }

  getPlatform () {
    return {
      darwin: 'osx',
      win32: 'win',
    }[process.platform] || process.platform
  }

  getSystemPath (name) {
    return window['getSystemPath'](name)
  }

  hasRestart () {
    return true
  }

  restart () {
    this.logger.info('Restarting extension')
    window.location.href = `file://${window['dirname']}/index.html`
  }

  async open (item) {
    try {
      if (item.asset?.type === 'fcpxml') {
        await this.importEditorProject(item.mainfile.id)
      } else {
        const subclips = await this.querySubclipImport([item])
        await this.importAsset(item.asset, subclips)
      }
    } catch (error) {
      this.notify.error('Error while opening in resolve', error)
    }
  }

  async importAsset (asset, subclips?: Subclip[], path?: string) {
    if (!path) {
      const response = await this.api.private.locateFile({ locateEndpointRequest: { asset: asset.id } })
      if (!response.workspace || !response.path) {
        this.logger.warn('Asset is outside workspaces')
        throw new Error('Asset is outside workspaces')
      }
      path = response.path
    }
    const result = await this.importFile(path)
    await this.importComments(asset, result)

    if (subclips?.length) {
      this.importSubclips(result, subclips)
    }
  }

  async importBundles (bundles) {
    const subclips = await this.querySubclipImport(bundles)

    for (const bundle of bundles) {
      if (bundle.asset?.type === 'fcpxml') {
        await this.importEditorProject(bundle.mainfile.id)
      } else if (['video', 'audio', 'image'].includes(bundle.asset.type)) {
        try {
          const bundleSubclips = subclips.filter(item => item.asset.id === bundle.asset.id)
          await this.importAsset(bundle.asset, bundleSubclips, bundle.mainfile.fullPath)
        } catch (error) {
          this.notify.error('Error while opening in resolve', error)
        }
      }
    }
  }

  getFolder (folder, name) {
    const subfolders = folder.GetSubFolderList()
    for (const subfolder of subfolders) {
      if (subfolder.GetName() === name) {
        return subfolder
      }
    }

    return this.pool.AddSubFolder(folder, name)
  }

  async importDirs (dirs, folder) {
    folder = folder || this.pool.GetCurrentFolder()

    for (const dir of dirs) {
      const subfolder = this.getFolder(folder, dir.name)
      this.pool.SetCurrentFolder(subfolder)

      await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
        if (bundles.length) {
          await this.importBundles(bundles)
        }
        if (directories.length) {
          await this.importDirs(directories, subfolder)
        }
      })
    }

    this.pool.SetCurrentFolder(folder)
  }

  importPath (path: string) {
    const pool = this.project.GetMediaPool()
    if (path.endsWith('.xml')) {
      const timeline = pool.ImportTimelineFromFile(path)
      if (!timeline) {
        throw Error('Resolve could not import this file')
      }
      return timeline
    } else {
      const items = pool.ImportMedia(path)
      if (!items.length) {
        throw Error('Resolve could not import this file')
      }
      return items
    }
  }

  async importComments (asset, items) {
    const comments = await this.api.mediaLibrary.getAllComments({
      asset: asset.id,
      forRoot: this.currentMediaRootID,
    })

    const markers = await this.api.mediaLibrary.getAllMarkers({
      asset: asset.id,
    })
    markers.sort((a, b) => a.tIn - b.tIn)

    for (const mediaItem of items) {
      for (const comment of comments) {
        let text = comment.text!
        if (comment.drawing) {
          text += '[Drawing]'
          text = text.trimLeft()
        }

        mediaItem.AddMarker(
          (comment.time ?? 0) * asset.info.fps,
          'Lemon',
          comment.user!.displayName,
          text,
          1.0,
        )
      }

      for (const marker of markers) {
        mediaItem.AddMarker(
          marker.tIn * asset.info.fps,
          'Yellow',
          marker.title,
          marker.text,
          (marker.tOut - marker.tIn) * asset.info.fps,
        )
      }
    }
  }

  async importSubclips (items, subclips: Subclip[]) {
    console.log('Start import subclip')
    items.forEach(mediaPoolItem => {
      const fps = parseFloat(mediaPoolItem.GetClipProperty('FPS'))
      subclips.forEach(subclip => {
        const start = Math.round(subclip.tIn * fps)
        const end = Math.round(subclip.tOut * fps)
        let name = `${subclip.name}`
        const folder = this.pool.GetCurrentFolder()
        const clipNames = folder.GetClipList().map(item => item.GetName())
        if (clipNames.includes(name)) {
          name = `${subclip.name} - Subclip ${subclip.id}`
        }

        this.pool.CreateTimelineFromClips(name, [{
          mediaPoolItem,
          startFrame: start,
          endFrame: end,
        }])
      })
    })
  }

  async getActiveSequencePaths () {
    const timeline = this.project.GetCurrentTimeline()
    if (!timeline) {
      return []
    }

    const items = new Set<string>([])

    for (const type of ['video', 'audio']) {
      for (let i = 1; i <= timeline.GetTrackCount(type); i++) {
        const trackitems = timeline.GetItemListInTrack(type, i)
        for (const item of trackitems) {
          const path = item.GetMediaPoolItem().GetClipProperty('File Path')
          items.add(path)
        }
      }
    }

    return [...items]
  }

  private getClips (folders) {
    let clips: any[] = []
    folders.forEach(folder => {
      clips = clips.concat(folder.GetClipList())
      clips = clips.concat(this.getClips(folder.GetSubFolderList()))
    })
    return clips
  }

  async getProjectPaths () {
    const pool = this.project.GetMediaPool()
    const folder = pool.GetRootFolder()
    const clips = this.getClips([folder])
    return clips.map(item => item.GetClipProperty('File Path')).filter(Boolean)
  }

  async attachProxies (pathProxyMap) {
    const pool = this.project.GetMediaPool()
    const folder = pool.GetRootFolder()
    const clips = this.getClips([folder])
      .map(item => [item.GetClipProperty('File Path'), item])
      .filter(item => item[0])

    clips.forEach(item => {
      const [path, clip] = item
      if (path in pathProxyMap) {
        clip.LinkProxyMedia(pathProxyMap[path])
      }
    })
  }

  async getLocalPath (options) {
    const { BrowserWindow, dialog } = window['nodeRequire']('electron').remote

    const result = await dialog.showOpenDialog(
      BrowserWindow.getFocusedWindow(), options)
    if (result.filePaths.length) {
      return result.filePaths[0]
    }
  }

  getConfig (key) {
    return window['config']?.get(key)
  }

  setConfig (key, value) {
    return window['config']?.set(key, value)
  }

  hasConfig () {
    return !!window['config']
  }

  async isProxyMode () {
    return Boolean(
      parseInt(this.project.GetSetting('perfProxyMediaMode')) ||
      parseInt(this.project.GetSetting('perfProxyMediaOn')),
    )
  }

  setProxyMode (enabled) {
    this.project.SetSetting('perfProxyMediaOn', (enabled ? 1 : 0).toString())
    this.project.SetSetting('perfProxyMediaMode', (enabled ? 1 : 0).toString())
  }

  getProject () {
    return this.projectmanager.GetCurrentProject()
  }
}
